import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { Layout } from "./components/Layout";
import { AuthGate } from "./components/AuthGate";
import { PublicLayout } from "./components/public/PublicLayout";
import { RouteErrorElement } from "./components/RootErrorBoundary";
import { Inbox } from "./pages/Inbox";
import { ExpensesHub } from "./pages/ExpensesHub";
import { ExpenseDetail } from "./pages/ExpenseDetail";
import { InvoicesHub } from "./pages/InvoicesHub";
import { AuditDashboard } from "./pages/AuditDashboard";
import { Exports } from "./pages/Exports";
import { Assistant } from "./pages/Assistant";
import { Reports } from "./pages/Reports";
import { TaxPolicies } from "./pages/TaxPolicies";
import { Import } from "./pages/Import";
import { Mileage } from "./pages/Mileage";
import { Login } from "./pages/Login";
import { AuthCallback } from "./pages/AuthCallback";
import { Settings } from "./pages/Settings";
// Wave 21: Review renders embedded inside Inbox (tab=pending).
// /review is kept as a redirect route below for old bookmarks.
import { AuditShareView } from "./pages/AuditShareView";
import { Vendors } from "./pages/Vendors";
import { VendorDetail } from "./pages/VendorDetail";
import { WaitlistPending } from "./pages/WaitlistPending";
import { AdminWaitlist } from "./pages/AdminWaitlist";
import { Landing } from "./pages/public/Landing";
import { Pricing } from "./pages/public/Pricing";
import { Features } from "./pages/public/Features";
import { Terms } from "./pages/public/Terms";
import { Privacy } from "./pages/public/Privacy";
// The crash module is statically imported, but we only register its route
// when `import.meta.env.DEV` is true. Vite resolves `import.meta.env.DEV` at
// build time, so in a production build the `if` below is dead-stripped and
// Crash is tree-shaken out of the bundle.
import { Crash } from "./pages/_crash";

const appShellChildren: RouteObject[] = [
  { path: "/inbox", Component: Inbox },
  // Wave 21: /review renders embedded inside /inbox (tab=pending).
  // Keep /review as a redirect so old bookmarks land in the right place.
  { path: "/review", element: <Navigate to="/inbox?tab=pending" replace /> },
  { path: "/expenses", Component: ExpensesHub },
  { path: "/expenses/:id", Component: ExpenseDetail },
  { path: "/invoices", Component: InvoicesHub },
  { path: "/audit", Component: AuditDashboard },
  { path: "/policies", Component: TaxPolicies },
  { path: "/exports", Component: Exports },
  { path: "/assistant", Component: Assistant },
  { path: "/reports", Component: Reports },
  { path: "/vendors", Component: Vendors },
  { path: "/vendors/:id", Component: VendorDetail },
  { path: "/import", Component: Import },
  { path: "/mileage", Component: Mileage },
  { path: "/settings", Component: Settings },
  // Platform-admin-only. Lives INSIDE the shell so admins keep the sidebar
  // + topbar while managing waitlist/admins. The page renders its own
  // "Not authorized" notice for non-admins, so the route is still safe to
  // expose through AuthGate — platform admins always have allowlisted
  // status, so AuthGate won't bounce them.
  { path: "/admin/waitlist", Component: AdminWaitlist },
];

if (import.meta.env.DEV) {
  appShellChildren.push({ path: "/_crash", Component: Crash });
}

export const router = createBrowserRouter([
  // Bare public routes — no marketing shell. Kept separate because
  // /login must not show the public header/footer, and /audit-share/:token
  // is consumed by external reviewers via a signed link.
  { path: "/login", Component: Login, ErrorBoundary: RouteErrorElement },
  {
    path: "/auth/callback",
    Component: AuthCallback,
    ErrorBoundary: RouteErrorElement,
  },
  {
    path: "/audit-share/:token",
    Component: AuditShareView,
    ErrorBoundary: RouteErrorElement,
  },
  // Alpha gate — sits OUTSIDE AuthGate so a non-allowlisted user can land
  // here without the gate looping them back. The page itself redirects to
  // /login if there's no session, and shows a "your account is ready" CTA
  // to allowlisted users who happened to land here from a stale tab.
  {
    path: "/waitlist-pending",
    Component: WaitlistPending,
    ErrorBoundary: RouteErrorElement,
  },

  // Marketing shell (nav + footer). The landing page at "/" is accessible
  // whether or not the viewer is signed in — its CTAs adapt.
  {
    Component: PublicLayout,
    ErrorBoundary: RouteErrorElement,
    children: [
      { path: "/", Component: Landing },
      { path: "/pricing", Component: Pricing },
      { path: "/features", Component: Features },
      { path: "/terms", Component: Terms },
      { path: "/privacy", Component: Privacy },
    ],
  },

  // Authenticated app shell. AuthGate → Layout → page. The top-level
  // ErrorBoundary catches throws in AuthGate, Layout, and every descendant
  // route — so we don't have to repeat it per-child.
  {
    Component: AuthGate,
    ErrorBoundary: RouteErrorElement,
    children: [
      {
        Component: Layout,
        children: appShellChildren,
      },
    ],
  },
]);
